import {
  QueryKeyT,
  useFetch_old,
  usePut_old,
  useFetch,
  useEdit,
  usePaginated,
  useGenericMutation,
  useCreate,
} from '../react-query';
import * as url from '../../urlHelper';
import { apiClient } from '../../apiHelper';
import { AxiosResponse } from 'axios';
import { serversQueryKeys } from './servers-query-keys';

export function useGetPaginatedServers(
  page: number,
  itemsPerPage: number,
  filters: Record<string, any> = {},
  config = {}
) {
  const getPaginatedServersFn = async (p: number = page): Promise<any> => {
    const params = new URLSearchParams({
      page: p.toString(),
      items_per_page: itemsPerPage.toString(),
    });

    if (Object.keys(filters).length > 0) {
      params.append('filters', JSON.stringify(filters));
    }

    const response = await apiClient.get(`/servers?${params.toString()}`);
    return response.data;
  };

  return usePaginated(
    getPaginatedServersFn,
    ['paginated-servers', ...serversQueryKeys.pagination(page, filters)],
    page,
    config
  );
}

export function useCreateServer(
  onSuccess?: (response: AxiosResponse, data: any) => void,
  onError?: (response: AxiosResponse, error: Error) => void,
  notification: boolean = false
) {
  const createServerFn = async (data: any) => {
    const response = await apiClient.post(`/servers`, data);
    return response;
  };

  return useCreate(
    createServerFn,
    ['create-server', ...serversQueryKeys.all.map(String)],
    'Error while creating server',
    'Server created successfully',
    onSuccess,
    onError,
    notification
  );
}

export function useCreateRandomServer(
  onSuccess?: (response: AxiosResponse, data: any) => void,
  onError?: (response: AxiosResponse, error: Error) => void,
  notification: boolean = true
) {
  const createRandomServerFn = async (data: any) => {
    const response = await apiClient.post(`/servers/random`, data);
    return response;
  };

  return useCreate(
    createRandomServerFn,
    ['random-server', ...serversQueryKeys.all.map(String)],
    'Error while creating random server',
    'Random server created successfully',
    onSuccess,
    onError,
    notification
  );
}

export function useDeleteServer(
  onSuccess?: (response: AxiosResponse, data: any) => void,
  onError?: (response: AxiosResponse, error: Error) => void,
  notification: boolean = true
) {
  const deleteServerFn = async (data: any) => {
    const response = await apiClient.delete(`/servers/${data.id}`);
    return response;
  };

  return useGenericMutation(
    deleteServerFn,
    ['delete-server', ...serversQueryKeys.all.map(String)],
    'Error while deleting server',
    'Server deleted successfully',
    undefined,
    onSuccess,
    onError,
    notification
  );
}

export function useTestServerConnection(
  onSuccess?: (response: AxiosResponse, data: any) => void,
  onError?: (response: AxiosResponse, error: Error) => void,
  notification: boolean = false
) {
  const testConnectionFn = async (data: any) => {
    const response = await apiClient.post(`/dicom/connection/test`, {
      server_id: data.server_id,
    });
    return response;
  };

  return useGenericMutation(
    testConnectionFn,
    ['test-server-connection', ...serversQueryKeys.all.map(String)],
    'Error while testing server connection',
    'Server connection test completed',
    undefined,
    onSuccess,
    onError,
    notification
  );
}

export function useGetCStoreServers(studyInstanceUid?: string, config = {}) {
  const getCStoreServersFn = async (): Promise<any> => {
    const params = new URLSearchParams({
      filters: JSON.stringify({ supports_c_store: true }),
    });

    if (studyInstanceUid) {
      params.append('study_instance_uid', studyInstanceUid);
    }

    const response = await apiClient.get(`/servers?${params.toString()}`);
    return response.data;
  };

  return useFetch(
    getCStoreServersFn,
    ['c-store-servers', studyInstanceUid || 'all', ...serversQueryKeys.all],
    config
  );
}
